import type { Box } from './print-layout.js'

/**
 * Фигуры печатного листа (D64): точки многоугольников в миллиметрах.
 *
 * Рамки рисует SVG поверх листа, а не фон блоков. Причина печатная:
 * фон браузер по умолчанию на бумагу не выводит («Фоновая графика»
 * выключена почти у всех), а контур и заливка SVG — содержимое
 * страницы и печатаются всегда. Рамка, которая пропадает при печати
 * из-за галочки в чужом диалоге, — это пустой лист с цифрами.
 *
 * Словарь фигур — тот же, что у образца: скошенный угол, двойной
 * контур, вытянутый восьмиугольник под число, лента подписи
 * с острым концом.
 */

/** Толщины линий в миллиметрах: пункты образца, пересчитанные (1 pt = 0.3528 мм). */
export const STROKE = {
  /** 2 pt: внешний контур больших рамок. */
  heavy: 0.71,
  /** 1.5 pt: поля под число. */
  medium: 0.53,
  /** 1 pt: внутренние деления. */
  light: 0.35,
  /** 0.75 pt: внутренний контур двойной рамки. */
  hair: 0.26,
  /** 0.5 pt: линейки строк. */
  thin: 0.18,
} as const

/** Отступ внутреннего контура двойной рамки от внешнего. */
export const INNER_GAP = 1.2

/** Срез угла по углам: левый верхний, правый верхний, правый нижний, левый нижний. */
export type Cuts = readonly [tl: number, tr: number, br: number, bl: number]

function cutsOf(cut: number | Cuts): Cuts {
  return typeof cut === 'number' ? [cut, cut, cut, cut] : cut
}

function round(value: number): number {
  return Math.round(value * 100) / 100
}

function points(list: readonly (readonly [number, number])[]): string {
  return list.map(([x, y]) => `${round(x)},${round(y)}`).join(' ')
}

/** Прямоугольник со скошенными углами: срез 0 — прямой угол. */
export function chamfered([x, y, w, h]: Box, cut: number | Cuts): string {
  const [tl, tr, br, bl] = cutsOf(cut)
  return points([
    [x + tl, y],
    [x + w - tr, y],
    [x + w, y + tr],
    [x + w, y + h - br],
    [x + w - br, y + h],
    [x + bl, y + h],
    [x, y + h - bl],
    [x, y + tl],
  ])
}

/**
 * Внутренний контур: та же фигура на `gap` внутрь.
 *
 * Срез уменьшается не на `gap`, а на `gap · (2 − √2)`: тогда скос
 * внутреннего контура идёт параллельно внешнему на том же расстоянии,
 * что и прямые стороны. С тем же срезом углы двойной рамки «толстели» бы.
 */
export function inset([x, y, w, h]: Box, gap: number): Box {
  return [x + gap, y + gap, w - 2 * gap, h - 2 * gap]
}

export function insetCuts(cut: number | Cuts, gap: number): Cuts {
  const shrink = gap * (2 - Math.SQRT2)
  const one = (value: number) => (value === 0 ? 0 : Math.max(0.3, value - shrink))
  const [tl, tr, br, bl] = cutsOf(cut)
  return [one(tl), one(tr), one(br), one(bl)]
}

/**
 * Лента подписи: левый край прямой — он прячется под полем числа,
 * правый заострён, как у образца.
 */
export function ribbon([x, y, w, h]: Box): string {
  const tip = h / 2
  return points([
    [x, y],
    [x + w - tip, y],
    [x + w, y + h / 2],
    [x + w - tip, y + h],
    [x, y + h],
  ])
}

/**
 * Рамка характеристики: верх срезан под выступ, где стоит значение,
 * низ чуть скошен. Выступ — трапеция внутри рамки, как у образца.
 */
export function abilityOutline([x, y, w, h]: Box, top = 5.5, bottom = 2): string {
  return points([
    [x, y + top],
    [x + top, y],
    [x + w - top, y],
    [x + w, y + top],
    [x + w, y + h - bottom],
    [x + w - bottom, y + h],
    [x + bottom, y + h],
    [x, y + h - bottom],
  ])
}

/** Выступ значения внутри рамки характеристики. */
export function abilityTab([x, y, w]: Box): string {
  const top = 5.5
  return points([
    [x + top, y],
    [x + top + 2.6, y + 4.6],
    [x + w - top - 2.6, y + 4.6],
    [x + w - top, y],
  ])
}

/** Где кончается выступ: по этой коробке ставится значение характеристики. */
export function abilityTabBox([x, y, w]: Box): Box {
  const top = 5.5
  return [x + top + 1.6, y + 0.2, w - 2 * top - 3.2, 4.2]
}

/**
 * Лента имени персонажа: волна сверху и снизу, как у образца.
 * Нижняя кромка — та же волна, сдвинутая на высоту ленты.
 */
export function bannerPath([x, y, w, h]: Box): string {
  const wave = (dy: number) =>
    `C ${round(x + w * 0.3)},${round(y + dy - 2.2)} ${round(x + w * 0.62)},${round(y + dy + 3.4)} ${round(x + w)},${round(y + dy - 0.4)}`
  const back = (dy: number) =>
    `C ${round(x + w * 0.62)},${round(y + dy + 3.4)} ${round(x + w * 0.3)},${round(y + dy - 2.2)} ${round(x)},${round(y + dy + 1)}`
  return [
    `M ${round(x)},${round(y + 1)}`,
    wave(0),
    `L ${round(x + w)},${round(y + h - 0.4)}`,
    back(h),
    'Z',
  ].join(' ')
}

/** Поле рисунка иконки game-icons.net: квадрат 512 × 512. */
export const ICON_BOUNDS: Box = [0, 0, 512, 512]

/**
 * Иконка в коробку листа: масштаб одинаковый по обеим осям,
 * рисунок по центру коробки. `bounds` — где рисунок лежит внутри
 * своих 512 × 512: щит, например, узкий и высокий, и вписывать его
 * надо по нему самому, а не по пустому квадрату вокруг.
 *
 * `k` нужен толщине линии: контур рисуется в единицах иконки,
 * и 0.5 мм на листе — это `0.5 / k` у неё.
 */
export function iconFit(
  [x, y, w, h]: Box,
  scale = 1,
  [bx, by, bw, bh]: Box = ICON_BOUNDS,
): { readonly transform: string; readonly k: number } {
  const k = Math.min(w / bw, h / bh) * scale
  const dx = x + w / 2 - (bx + bw / 2) * k
  const dy = y + h / 2 - (by + bh / 2) * k
  return { transform: `translate(${round(dx)} ${round(dy)}) scale(${k.toFixed(5)})`, k }
}

/** Путь иконки из файла SVG: в наборе у каждой иконки один `path`. */
export function iconPath(svg: string): string {
  const found = /<path[^>]*\sd="([^"]+)"/.exec(svg)
  if (found?.[1] === undefined) throw new Error('в иконке нет пути')
  return found[1]
}
